<template>
    <Combobox class="mt-8" as="div" v-model="state.selected_account" @update:modelValue="state.search_string = ''">
        <ComboboxLabel class="block text-sm font-medium leading-6 text-gray-900">Assigned to</ComboboxLabel>
        <div class="relative mt-2">
            <ComboboxInput class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" @change="state.search_string = $event.target.value" @blur="state.search_string = ''" :display-value="(account) => account?.name" placeholder="Account name or code" />
            <ComboboxButton v-if="filtered_accounts.length > 0" class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z" clip-rule="evenodd" />
                </svg>
            </ComboboxButton>

            <ComboboxOptions v-if="filtered_accounts.length > 0" class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                <ComboboxOption v-for="account in filtered_accounts" :key="account.id" :value="account" as="template" v-slot="{ active, selected }">
                    <li :class="['relative cursor-default select-none py-2 pl-3 pr-9 hover:cursor-pointer', active ? 'bg-blue-600 text-white' : 'text-gray-900']">
                        <span :class="['block truncate', selected && 'font-semibold']">
                            {{ account.name }}
                        </span>
                        <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-blue-600']">
                            <CheckIcon class="h-4 w-4" />
                        </span>
                    </li>
                </ComboboxOption>
            </ComboboxOptions>
        </div>
    </Combobox>

    <Message :active="state.results_message_active && state.search_string.length > 0 && filtered_accounts.length === 0" heading="No accounts match your search" type="error" class="mt-4" />
</template>

<script>
import { reactive, computed, watch, onMounted, ref } from "vue";
import { useAccountStore } from "@sto/account.store"

import CheckIcon from "@ico/CheckIcon.vue"
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/vue'

export default {
    components: {
        Combobox,
        ComboboxButton,
        ComboboxInput,
        ComboboxLabel,
        ComboboxOption,
        ComboboxOptions,
        CheckIcon
    },
    props: ['auctioneer_id'],
    setup(props, {emit}) {

        const ACCOUNT_STORE = useAccountStore()

        const state = reactive({
            accounts_loading: false,
            account_options: [],
            search_string: '',
            selected_account: null,
            account_timeout: null,
            results_message_active: false
        })

        const getEligibleParentAccounts = async () => {

            if (state.accounts_loading) return

            state.accounts_loading = true

            try {

                const accounts_data = await ACCOUNT_STORE.list([
                    { key: 'per_page', value: 100 },
                    { key: 'page', value: 1 },
                    { key: 'auctioneer_id', value: props.auctioneer_id },
                    { key: 'search_string', value: state.search_string },
                    { key: 'no_parent', value: true },
                    { key: 'type', value: 'trade,main,auctioneer'}
                ])

                if (!Array.isArray(accounts_data?.accounts)) return

                state.account_options = accounts_data.accounts.filter(acc => acc.parent_id === null).map(({id, name, code}) => {
                    return {
                        id,
                        name: `${name} (${code})`
                    }
                })

                state.results_message_active = true

            } catch (error) {
                console.error('error', error)

            } finally {
                state.accounts_loading = false
            }
        }

        
        const filtered_accounts = computed(() => {
            return state.search_string === ''
                ? state.account_options
                : state.account_options.filter((account) => {
                    return account.name.toLowerCase().includes(state.search_string.toLowerCase())
                })
        })

        watch(
            () => state.search_string,
            (new_value) => {
                state.results_message_active = false
                if (state.account_timeout) clearTimeout(state.account_timeout)
                state.account_timeout = setTimeout(getEligibleParentAccounts, 500)
            }
        )

        watch(
            () => state.selected_account,
            (new_value) => {
                emit('value_update', state.selected_account?.id || null)
            }
        )

        onMounted(() => {
            getEligibleParentAccounts()
        })

        return {
            state,
            filtered_accounts
        }
    }
}
</script>